import { openDB, type IDBPDatabase } from 'idb';

/**
 * Local recovery journal in IndexedDB. Keyed by `${userId}:${projectId}` so a different user on the
 * same browser never sees another user's unsaved work. Local entries are NEVER reported as server-saved.
 */
export interface JournalEntry {
  key: string;
  userId: string;
  projectId: string;
  baseRevision: number;
  document: unknown;
  updatedAt: number;
}

let dbp: Promise<IDBPDatabase> | null = null;
function db() {
  dbp ??= openDB('scs-recovery', 1, {
    upgrade(d) {
      const s = d.createObjectStore('journal', { keyPath: 'key' });
      s.createIndex('userId', 'userId');
    },
  });
  return dbp;
}

export const journalKey = (userId: string, projectId: string) => `${userId}:${projectId}`;

export async function writeJournal(e: Omit<JournalEntry, 'key' | 'updatedAt'>): Promise<void> {
  try {
    await (await db()).put('journal', { ...e, key: journalKey(e.userId, e.projectId), updatedAt: Date.now() });
  } catch {
    // Private mode / quota: recovery is best effort; the save status still reflects the server truth.
  }
}

export async function readJournal(userId: string, projectId: string): Promise<JournalEntry | undefined> {
  try {
    return await (await db()).get('journal', journalKey(userId, projectId));
  } catch {
    return undefined;
  }
}

export async function clearJournal(userId: string, projectId: string): Promise<void> {
  try {
    await (await db()).delete('journal', journalKey(userId, projectId));
  } catch {
    /* ignore */
  }
}
